import { motion } from 'framer-motion';
import { Code2, Bot, ListTodo, ArrowRight } from 'lucide-react';

interface Competitor {
  name: string;
  icon: React.ElementType;
  theirClaim: string;
  theirLimit: string;
  ourReframe: string;
  color: string;
}

const competitors: Competitor[] = [
  {
    name: 'vs Cursor',
    icon: Code2,
    theirClaim: '"AI-powered code editor"',
    theirLimit: 'Cursor writes code. One file at a time. One conversation at a time.',
    ourReframe: 'We orchestrate engineering. Parallel agents, persistent context, cross-repo awareness.',
    color: '#3b82f6'
  },
  {
    name: 'vs Devin',
    icon: Bot,
    theirClaim: '"Autonomous AI engineer"',
    theirLimit: 'Black box execution. You see the output, not the process. No guardrails.',
    ourReframe: 'Glass box, not black box. Every step visible. Human approval at decision points.',
    color: '#8b5cf6'
  },
  {
    name: 'vs Jira',
    icon: ListTodo,
    theirClaim: '"Issue tracking for teams"',
    theirLimit: 'Tracks what humans do. Doesn\'t do the work. Linear lists, not execution graphs.',
    ourReframe: 'The issue tracker that executes. Tasks have agents. Work flows automatically.',
    color: '#10b981'
  }
];

export function Differentiation() {
  return (
    <section className="py-24 bg-surface-900">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
            The Missing Middle
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Between AI autocomplete and autonomous agents, there's orchestration.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {competitors.map((competitor, index) => (
            <motion.div
              key={competitor.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group"
            >
              <div className="relative h-full bg-surface-800 rounded-xl border border-surface-600 overflow-hidden hover:border-surface-500 transition-all">
                {/* Header */}
                <div className="p-6 border-b border-surface-700">
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: `${competitor.color}20` }}
                    >
                      <competitor.icon className="w-5 h-5" style={{ color: competitor.color }} />
                    </div>
                    <span className="font-semibold text-text-primary">{competitor.name}</span>
                  </div>
                  <p className="text-sm text-text-tertiary italic">{competitor.theirClaim}</p>
                </div>

                {/* Their Limitation */}
                <div className="p-6 border-b border-surface-700 bg-surface-800/50">
                  <div className="flex items-start gap-2 mb-2">
                    <span className="text-xs font-medium text-red-400 uppercase tracking-wider">Their limit</span>
                  </div>
                  <p className="text-sm text-text-secondary">{competitor.theirLimit}</p>
                </div>

                {/* Our Reframe */}
                <div className="p-6 bg-gradient-to-b from-surface-800 to-surface-800/50">
                  <div className="flex items-start gap-2 mb-2">
                    <span className="text-xs font-medium text-primary-400 uppercase tracking-wider">Our approach</span>
                  </div>
                  <p className="text-sm text-text-primary font-medium">{competitor.ourReframe}</p>
                </div>

                {/* Hover accent */}
                <div
                  className="absolute bottom-0 left-0 right-0 h-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ backgroundColor: competitor.color }}
                />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Summary line */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="inline-flex items-center gap-4 px-6 py-4 bg-surface-800 rounded-xl border border-surface-600">
            <span className="text-text-secondary">AI autocomplete</span>
            <ArrowRight className="w-4 h-4 text-text-tertiary" />
            <span className="text-primary-400 font-semibold">Orchestration</span>
            <ArrowRight className="w-4 h-4 text-text-tertiary" />
            <span className="text-text-secondary">Autonomous agents</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
